(() => {
  const apiBase = 'https://clvaspfvdsjwotmfhrgy.supabase.co';
  const organizationId = '4ce8c3ec-a527-4165-859d-63891a404b33';
  const publicKey = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImNsdmFzcGZ2ZHNqd290bWZocmd5Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzEzMzc0NDksImV4cCI6MjA4NjkxMzQ0OX0.TQuiqUV7_SA676CXHXIGNamFrM2-KFliJdZav2wPEVc';

  class EventTicketClient {
    constructor({ fetch: request = globalThis.fetch, timeout = 8000, ttl = 15000 } = {}) {
      Object.assign(this, { request: request.bind(globalThis), timeout, ttl });
      this.cache = new Map();
    }

    async json(path, body) {
      const key = path + JSON.stringify(body || null);
      const cached = this.cache.get(key);
      if (cached && cached.expires > Date.now()) return cached.promise;
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), this.timeout);
      const promise = this.request(new URL(path, apiBase).href, {
        method: body ? 'POST' : 'GET',
        credentials: 'omit',
        headers: {
          Accept: 'application/json', apikey: publicKey, Authorization: `Bearer ${publicKey}`,
          ...(body ? { 'Content-Type': 'application/json' } : {})
        },
        signal: controller.signal,
        ...(body ? { body: JSON.stringify(body) } : {})
      }).then(async (response) => {
        if (!response.ok) throw new Error('Event ticket request failed');
        const rows = await response.json();
        if (!Array.isArray(rows)) throw new Error('Invalid event ticket response');
        return rows;
      }).catch((error) => {
        if (this.cache.get(key)?.promise === promise) this.cache.delete(key);
        throw error;
      }).finally(() => clearTimeout(timer));
      this.cache.set(key, { promise, expires: Date.now() + this.ttl });
      return promise;
    }

    async findEvent(productId) {
      if (!/^\d+$/.test(String(productId))) throw new Error('Invalid product ID');
      const parameters = new URLSearchParams({
        organization_id: `eq.${organizationId}`,
        shopify_product_id: `eq.${productId}`,
        select: 'id,organization_id,shopify_product_id,capacity,start_time,end_time', limit: '2'
      });
      const rows = await this.json(`/rest/v1/events_public?${parameters}`);
      if (rows.length !== 1) return null;
      const event = rows[0];
      if (!event || event.organization_id !== organizationId || String(event.shopify_product_id) !== String(productId) ||
          !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(event.id)) return null;
      return event;
    }

    async count(eventId) {
      const rows = await this.json('/rest/v1/rpc/get_registration_counts', { _event_ids: [eventId] });
      if (!rows.length) return 0;
      if (rows.length !== 1 || rows[0]?.event_id !== eventId) throw new Error('Invalid registration count');
      const raw = rows[0].seats;
      if ((typeof raw !== 'number' && typeof raw !== 'string') || String(raw).trim() === '') throw new Error('Missing registration count');
      const count = Number(raw);
      if (!Number.isSafeInteger(count) || count < 0) throw new Error('Invalid registration count');
      return count;
    }
  }

  const ElementBase = typeof HTMLElement === 'undefined' ? class {} : HTMLElement;

  class EventTicketStatus extends ElementBase {
    connectedCallback() {
      this.shipping = this.querySelector('[data-shipping-status]');
      this.content = this.querySelector('[data-ticket-content]');
      if (!this.shipping || !this.content) return;
      this.active = true;
      this.refresh();
    }

    disconnectedCallback() {
      this.active = false;
      clearTimeout(this.timer);
      this.requestId = (this.requestId || 0) + 1;
    }

    async refresh() {
      clearTimeout(this.timer);
      const requestId = this.requestId = (this.requestId || 0) + 1;
      const current = () => this.active && requestId === this.requestId;
      try {
        const event = await this.client.findEvent(this.dataset.productId);
        if (!current()) return;
        const previousEventId = this.event?.id;
        this.event = event;
        if (!event || previousEventId !== event.id || this.ended()) this.render(null);
        if (event && !this.ended()) {
          const count = await this.client.count(event.id);
          if (current()) this.render(count);
        }
      } catch (_error) {
        if (current()) this.render(null);
      } finally {
        if (current() && this.event !== null) {
          this.timer = setTimeout(() => {
            if (document.visibilityState === 'hidden') this.scheduleRefresh();
            else this.refresh();
          }, 60000);
        }
      }
    }

    scheduleRefresh() {
      this.timer = setTimeout(() => {
        if (document.visibilityState === 'hidden') this.scheduleRefresh();
        else this.refresh();
      }, 60000);
    }

    ended() {
      const timestamp = this.event?.end_time || this.event?.start_time;
      const end = timestamp ? Date.parse(timestamp) : NaN;
      return Number.isFinite(end) && end <= Date.now();
    }

    label(name, values = {}) {
      let text = this.dataset[name] || '';
      for (const [key, value] of Object.entries(values)) text = text.replaceAll(`[${key}]`, String(value));
      return text;
    }

    render(count) {
      const ticket = Boolean(this.event);
      this.shipping.hidden = ticket;
      this.content.hidden = !ticket;
      if (!ticket) return;
      const capacity = Number(this.event.capacity);
      const ended = this.ended();
      const hasCount = Number.isSafeInteger(count) && count >= 0 && !ended;
      const hasCapacity = Number.isSafeInteger(capacity) && capacity > 0;
      const key = JSON.stringify([this.event.id, hasCount ? count : null, capacity, ended]);
      if (key === this.renderKey) return;
      this.renderKey = key;
      const nodes = [];
      const create = (tag, className, text) => {
        const element = document.createElement(tag);
        element.className = className;
        if (text !== undefined) element.textContent = text;
        return element;
      };
      if (hasCount && hasCapacity) {
        const label = this.label('labelOccupied', { count, capacity });
        const meter = create('div', 'eventforge-calendar__availability-meter');
        meter.classList.toggle('eventforge-calendar__availability-meter--empty', count === 0);
        meter.setAttribute('role', 'meter');
        meter.setAttribute('aria-label', this.label('labelTicket'));
        meter.setAttribute('aria-valuemin', '0');
        meter.setAttribute('aria-valuemax', String(capacity));
        meter.setAttribute('aria-valuenow', String(Math.min(count, capacity)));
        meter.setAttribute('aria-valuetext', label);
        const fill = create('span', 'eventforge-calendar__availability-fill');
        fill.style.width = `${Math.min(count / capacity, 1) * 100}%`;
        fill.setAttribute('aria-hidden', 'true');
        const text = create('span', 'eventforge-calendar__availability-label', label);
        text.setAttribute('aria-hidden', 'true');
        meter.append(fill, text);
        nodes.push(meter, create('p', 'event-ticket-status__note', this.label('labelRemaining', { count: Math.max(0, capacity - count) })));
      } else {
        nodes.push(create('p', 'event-ticket-status__note', this.label(ended ? 'labelEnded' : hasCount ? 'labelRegistered' : 'labelTicket', { count })));
      }
      this.content.replaceChildren(...nodes);
    }
  }

  if (typeof module !== 'undefined') module.exports = { EventTicketClient, EventTicketStatus };
  if (typeof customElements === 'undefined' || customElements.get('event-ticket-status')) return;
  EventTicketStatus.prototype.client = new EventTicketClient();
  customElements.define('event-ticket-status', EventTicketStatus);
})();
